/**
 * StrippedProps<T>
 * Produces a readonly version of T where:
 * - Any key whose type includes undefined is omitted from the "required" portion
 * - Then we intersect back with Partial<T> so optional keys are still allowed on input
 *
 * In practice this lets stripDefaultProperties return an object typed like the original,
 * but without guaranteeing presence of fields that were empty and removed.
 * Without this special typing, the result would be a partial T that would break
 * consumers expecting the original shape with required properties.
 *
 * Example:
 * type Input  = { a: number; b?: string; c: string | undefined };
 * type Output = StrippedProps<Input>;
 *
 * const example: Input = {
 *   a: 1,
 *   b: 'hello',
 *   c: '',
 * };
 * const output = stripDefaultProperties(example);
 * // output => { a: 1, b: 'hello' }
 *
 * const exampleBad: Input = {
 *   a: 1,
 *   b: 'hello',
 *   c: '',
 * };
 * const outputBad = stripDefaultProperties({
 *   a: 1,
 *   c: '',
 * });
 */
type StrippedProps<T extends object> = { [K in keyof T as undefined extends T[K] ? never : K]: T[K] } & Partial<T>;

/**
 * Removes keys from an object whose values match any of the specified empty values (shallow compare).
 *
 * @template T - The type of the input object
 * @param obj - The object to strip empty properties from
 * @param defaultsToRemove - An array of values to consider as "empty" and remove. Defaults to `[undefined]`.
 *                      Can include `undefined`, `null`, empty string `''`, or `0`.
 * @returns A new object with empty properties removed. Keys whose values matched
 *          any value in `defaultsToRemove` are omitted from the result.
 *
 * @example
 * ```typescript
 * const input = { a: 1, b: undefined, c: 'hello', d: '' };
 * const output = stripDefaultProperties(input, [undefined, '']);
 * // output => { a: 1, c: 'hello' }
 * ```
 *
 * @example
 * ```typescript
 * const input = { a: 1, b: null, c: 0, d: '' };
 * const output = stripDefaultProperties(input, [null, 0]);
 * // output => { a: 1, d: '' }
 * ```
 */
export function stripDefaultProperties<T extends object>(obj: T, defaultsToRemove: ReadonlyArray<undefined | '' | null | 0> = [undefined]): StrippedProps<T> {
  const result: Partial<T> = {};

  (Object.keys(obj) as Array<keyof T>).forEach(key => {
    const value = obj[key];
    if (!defaultsToRemove.some(empty => value === empty)) {
      result[key] = value;
    }
  });

  return result as StrippedProps<T>;
}
